/**
 * [we] NIN Products – scan further collection pages via Section Rendering API
 * and append cards marked [data-nin-compatible] / [data-nin-product-card].
 */
(function () {
  function parseState(root) {
    const el = root.querySelector('[data-nin-scan-state]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function sectionHtmlFromResponse(payload, sectionId) {
    if (!payload || typeof payload !== 'object') return '';
    return payload[sectionId] || payload[String(sectionId)] || '';
  }

  function extractCards(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const sectionRoot = doc.querySelector('[data-nin-compatible-products]') || doc.body;
    const grid = sectionRoot.querySelector('[data-nin-grid]');
    if (!grid) return { cards: [], state: null };

    const stateScript = sectionRoot.querySelector('[data-nin-scan-state]');
    let state = null;
    if (stateScript) {
      try {
        state = JSON.parse(stateScript.textContent);
      } catch (e) {
        state = null;
      }
    }

    if (state && state.previewUnfiltered) {
      return { cards: Array.from(grid.querySelectorAll('[data-nin-product-card]')), state };
    }

    const cards = Array.from(grid.querySelectorAll('[data-nin-product-card]')).filter(function (card) {
      return card.getAttribute('data-nin-compatible') === 'true';
    });

    return { cards: cards, state: state };
  }

  class NinCompatibleProducts {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector('[data-nin-grid]');
      this.status = root.querySelector('[data-nin-status]');
      this.button = root.querySelector('[data-nin-load-more]');
      this.state = parseState(root);
      this.sectionId = root.dataset.sectionId;
      this.targetCount = Number(root.dataset.targetCount || (this.state && this.state.targetCount) || 8);
      this.loadMoreLabel = root.dataset.loadMoreLabel || 'Mehr laden';
      this.scanningLabel = root.dataset.scanningLabel || 'Weitere Produkte werden geprüft…';
      this.loading = false;
      this.seenIds = new Set();

      if (!this.grid || !this.state) return;

      this.grid.querySelectorAll('[data-product-id]').forEach(function (card) {
        this.seenIds.add(card.getAttribute('data-product-id'));
      }.bind(this));

      if (this.button) {
        this.button.addEventListener('click', function () {
          this.scanUntil(this.grid.querySelectorAll('[data-nin-product-card]').length + this.targetCount);
        }.bind(this));
      }

      var visible = this.grid.querySelectorAll('[data-nin-product-card]').length;
      if (visible < this.targetCount && this.state.currentPage < this.state.totalPages && !this.state.previewUnfiltered) {
        this.scanUntil(this.targetCount);
      } else {
        this.updateChrome();
      }
    }

    setStatus(message, show) {
      if (!this.status) return;
      if (!show || !message) {
        this.status.setAttribute('hidden', '');
        this.status.textContent = '';
        return;
      }
      this.status.textContent = message;
      this.status.removeAttribute('hidden');
    }

    updateChrome() {
      var visible = this.grid.querySelectorAll('[data-nin-product-card]').length;
      var hasMore = this.state.currentPage < this.state.totalPages;

      if (visible === 0 && !hasMore) {
        this.setStatus('Keine passenden Produkte gefunden.', true);
      } else if (visible === 0 && hasMore) {
        this.setStatus(this.scanningLabel, true);
      } else {
        this.setStatus('', false);
      }

      if (!this.button) return;
      if (!hasMore || this.state.previewUnfiltered) {
        this.button.setAttribute('hidden', '');
      } else {
        this.button.removeAttribute('hidden');
        this.button.disabled = false;
        this.button.textContent = this.loadMoreLabel;
      }
    }

    fetchPage(page) {
      var url = new URL(window.location.href);
      url.searchParams.set('sections', this.sectionId);
      url.searchParams.set('page', String(page));

      return fetch(url.toString(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      }).then(function (response) {
        if (!response.ok) throw new Error('Section render failed');
        return response.json();
      }.bind(this)).then(function (payload) {
        return sectionHtmlFromResponse(payload, this.sectionId);
      }.bind(this));
    }

    scanUntil(desiredCount) {
      if (this.loading || !this.state) return Promise.resolve();
      this.loading = true;
      if (this.button) {
        this.button.disabled = true;
        this.button.textContent = this.scanningLabel;
      }
      this.setStatus(this.scanningLabel, true);

      var self = this;

      function step() {
        if (
          self.grid.querySelectorAll('[data-nin-product-card]').length >= desiredCount ||
          self.state.currentPage >= self.state.totalPages
        ) {
          return Promise.resolve();
        }

        var nextPage = self.state.currentPage + 1;
        return self.fetchPage(nextPage).then(function (html) {
          var result = extractCards(html);
          var cards = result.cards;
          var state = result.state;

          if (state) {
            self.state.currentPage = state.currentPage || nextPage;
            self.state.totalPages = state.totalPages || self.state.totalPages;
          } else {
            self.state.currentPage = nextPage;
          }

          cards.forEach(function (card) {
            if (!document.contains(self.grid)) return;
            var id = card.getAttribute('data-product-id');
            if (id && self.seenIds.has(id)) return;
            if (id) self.seenIds.add(id);
            self.grid.appendChild(document.importNode(card, true));
          });

          if (!document.contains(self.grid)) return Promise.resolve();
          return step();
        });
      }

      return step()
        .catch(function () {
          self.setStatus('Produkte konnten nicht geladen werden. Bitte erneut versuchen.', true);
        })
        .then(function () {
          self.loading = false;
          self.updateChrome();
        });
    }
  }

  function init(root, options) {
    var force = options && options.force;
    var roots = root
      ? [root]
      : Array.prototype.slice.call(document.querySelectorAll('[data-nin-compatible-products]'));

    roots.forEach(function (el) {
      if (!el || !el.hasAttribute('data-nin-compatible-products')) return;
      if (!force && el.dataset.ninInit === '1') return;
      el.dataset.ninInit = '1';
      new NinCompatibleProducts(el);

      var sort = el.querySelector('[data-nin-sort]');
      var grid = el.querySelector('[data-nin-grid]');
      if (!sort || !grid || sort.dataset.ninSortBound === '1') return;
      sort.dataset.ninSortBound = '1';

      sort.addEventListener('change', function () {
        var liveGrid = el.querySelector('[data-nin-grid]');
        if (!liveGrid) return;
        var cards = Array.prototype.slice.call(liveGrid.querySelectorAll('[data-nin-product-card]'));
        var mode = sort.value;

        cards.sort(function (a, b) {
          var titleA = (a.querySelector('.product-card__title') || {}).textContent || '';
          var titleB = (b.querySelector('.product-card__title') || {}).textContent || '';
          var priceA = parseFloat(a.getAttribute('data-price') || '0');
          var priceB = parseFloat(b.getAttribute('data-price') || '0');

          if (mode === 'title-asc') return titleA.localeCompare(titleB);
          if (mode === 'title-desc') return titleB.localeCompare(titleA);
          if (mode === 'price-asc') return priceA - priceB;
          if (mode === 'price-desc') return priceB - priceA;
          return 0;
        });

        cards.forEach(function (card) {
          liveGrid.appendChild(card);
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var target = event && event.target;
    if (target && target.hasAttribute && target.hasAttribute('data-nin-compatible-products')) {
      init(target, { force: true });
      return;
    }
    if (target && target.querySelector) {
      var nested = target.querySelector('[data-nin-compatible-products]');
      if (nested) init(nested, { force: true });
    }
  });

  document.addEventListener('collection:reloaded', function () {
    document.querySelectorAll('[data-nin-compatible-products]').forEach(function (el) {
      init(el, { force: true });
    });
  });
})();
